import { isTransporterDepartureDay } from '@carsfinders/contracts';

import { t } from '../../../../lib/i18n';
import { cn } from '../../utils';
import { SessionBidLots } from './session-bid-lots';
import type { ScheduleDayGroup } from './session-schedule-types';
import {
  countdownLabel,
  dayDiff,
  formatListDayTitle,
  isCountdownUrgent,
  lotsLabel,
  sessionsLabel,
  statusLabel,
} from './session-schedule-utils';

/**
 * Design D's list view: one card per day, one row per session — admin port of
 * `apps/auction/.../components/AuctionScheduleListReadOnly.tsx`.
 *
 * Rows carry no destination link: the web original points at `/cars`, a
 * customer-catalogue route the admin SPA does not have
 * (`apps/admin/src/router.tsx` has no car-catalogue path, and the operator's
 * equivalent — the bid-relay desk — is keyed by bid, not by session). Rather
 * than link somewhere that does not correspond to what was clicked, the row
 * renders as an inert card. Same call as `ScheduleDayPanel`'s own web-side
 * note about `/cars` having no date filter — the honest option over one that
 * looks right and goes nowhere useful.
 */

interface SessionListViewProps {
  days: ScheduleDayGroup[];
  /** Today in the display zone, `YYYY-MM-DD`. */
  today: string;
}

export function SessionListView({ days, today }: SessionListViewProps) {
  /** «сьогодні» / «завтра» / «відправлення автовоза» / nothing. */
  const dayTag = (date: string): string => {
    const offset = dayDiff(today, date);
    if (offset === 0) return t('auctionSchedule.session.today');
    if (offset === 1) return t('auctionSchedule.session.tomorrow');
    return isTransporterDepartureDay(date) ? t('auctionSchedule.list.transporterDay') : '';
  };

  return (
    <section className="flex flex-col gap-3">
      {days.map((day) => {
        const offset = dayDiff(today, day.date);
        const soon = offset >= 0 && offset <= 1;
        const tag = dayTag(day.date);

        return (
          <div key={day.date} className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
            <div
              className={cn(
                'flex items-baseline justify-between gap-2 border-b border-cf-line-soft px-5 py-3',
                soon ? 'bg-cf-accent/8' : 'bg-cf-surface',
              )}
            >
              <div className="flex items-baseline gap-2.5">
                <strong className="text-[15px]">{formatListDayTitle(day.date)}</strong>
                {tag && (
                  <span
                    className={cn(
                      'font-mono text-[11px] tracking-[0.08em] uppercase',
                      soon ? 'text-cf-ok' : 'text-cf-ink-subtle',
                    )}
                  >
                    {tag}
                  </span>
                )}
              </div>
              <span className="text-[12px] text-cf-ink-subtle">
                {`${sessionsLabel(day.sessions.length, t)} · ${lotsLabel(day.lots, t)}`}
              </span>
            </div>

            {day.sessions.map((view) => (
              <div
                key={view.session.id}
                style={{ borderLeftColor: view.dot }}
                className="border-b border-l-[3px] border-cf-line-soft text-cf-ink"
              >
                <div className="grid grid-cols-[72px_12px_1fr_auto] items-center gap-3.5 px-5 py-3.5">
                  <span className="shrink-0 font-mono text-[15px]">{view.session.time}</span>
                  <span aria-hidden="true" className="h-2 w-2 rounded-cf-xs" style={{ background: view.dot }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold">{view.session.house.name}</span>
                    <span className="block text-[12px] text-cf-ink-subtle">
                      {lotsLabel(view.session.lots, t)}
                      {` · ${statusLabel(view, today, t)}`}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'font-mono text-[12px] whitespace-nowrap',
                      isCountdownUrgent(view, today) ? 'text-cf-ok' : 'text-cf-ink-subtle',
                    )}
                  >
                    {countdownLabel(view, today, t)}
                  </span>
                </div>
                {view.session.bidLots && view.session.bidLots.length > 0 && (
                  <div className="pr-5 pb-3.5 pl-[124px]">
                    <SessionBidLots lots={view.session.bidLots} />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {days.length === 0 && (
        <div className="rounded-cf-lg border border-cf-line bg-cf-surface p-10 text-center">
          <p className="text-[18px] font-semibold">{t('auctionSchedule.list.empty')}</p>
          <p className="mt-1.5 text-[14px] text-cf-ink-muted">{t('auctionSchedule.list.emptyHint')}</p>
        </div>
      )}
    </section>
  );
}
